import { WorkItemUserCategory } from '../__generated__/globalTypes';
import { PATHS } from '../paths';

import type { AlertRowAction } from './alertAction';
import type { WorkItemDetailsFragment } from '../components/alerts/__generated__/workItemFragment.gql';

/** The fields a work-item row action derives from. */
export type AlertActionWorkItem = Pick<WorkItemDetailsFragment, 'category' | 'bankConnection'>;

/**
 * A CONFIG work item carries no subtype — the relation is the discriminator:
 * a `bankConnection` names the connection to reconnect, and no relation means
 * the one CONFIG condition with no subject row, phone verification, whose fix
 * lives in settings.
 *
 * That relation-partitions-CONFIG property must stay total: today's two CONFIG
 * producers (unhealthy bank connection, unverified phone) split exactly on it,
 * and a producer that ports without a relation this router recognizes lands on
 * the phone-verification branch and sends the user to settings for the wrong
 * fix. A new CONFIG condition either carries a distinguishing relation or
 * extends this router first.
 */
const configAction = (workItem: AlertActionWorkItem): AlertRowAction =>
  workItem.bankConnection
    ? { kind: 'reconnectBank', bankConnectionId: workItem.bankConnection.id }
    : { kind: 'navigate', path: PATHS.SETTINGS };

/**
 * A new `WorkItemUserCategory` does not compile until it picks an action.
 * `null` means the row has none of its own, and the click belongs to whatever
 * the surface does with an ordinary alert.
 */
const ACTION_BY_CATEGORY: Record<WorkItemUserCategory, (workItem: AlertActionWorkItem) => AlertRowAction | null> = {
  [WorkItemUserCategory.Config]: configAction,
  [WorkItemUserCategory.Update]: () => null,
  [WorkItemUserCategory.Reminder]: () => null,
};

/**
 * The action a work-item row click runs, or `null` when the category has none
 * — the twin of `getAlertRowAction`, which answers `null` for every adapted
 * work item because the twin carries no subtype. Route work-item clicks here.
 */
export function getWorkItemAlertRowAction(workItem: AlertActionWorkItem): AlertRowAction | null {
  return ACTION_BY_CATEGORY[workItem.category](workItem);
}
